import {
  computed,
  onBeforeUnmount,
  onMounted,
  type ComputedRef,
} from 'vue'
import throttle from 'www/utils/decorators/throttle'
import smoothScrollTo from 'src/utils/smoothScrollTo'
import type { SectionMenuItem } from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/SectionMenu/types'
import { ITEM_SPY_OFFSET } from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/SectionMenu/composables/useSectionMenuScroll/constants'
import createSectionMenuHeader from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/SectionMenu/composables/useSectionMenuScroll/functions/createSectionMenuHeader'
import createSectionMenuSticky from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/SectionMenu/composables/useSectionMenuScroll/functions/createSectionMenuSticky'
import createSectionMenuScrollSpy from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/SectionMenu/composables/useSectionMenuScroll/functions/createSectionMenuScrollSpy'

const SCROLL_MS = 600
const RESIZE_THROTTLE_MS = 100

export default function useSectionMenuScroll(items: ComputedRef<SectionMenuItem[]>) {
  const header = createSectionMenuHeader()
  const sticky = createSectionMenuSticky(header.headerOffset)

  const spyOffset = computed(() => (
    header.headerOffset.value + sticky.menuHeight.value + ITEM_SPY_OFFSET
  ))

  const spy = createSectionMenuScrollSpy(items, { spyOffset })

  let resizeObserver: ResizeObserver | null = null
  let unlockTimer: ReturnType<typeof setTimeout> | null = null

  function setupAll() {
    header.refresh()
    sticky.setup()
    spy.setup()
  }

  const setupAllThrottled = throttle(setupAll, RESIZE_THROTTLE_MS)

  function clearUnlockTimer() {
    if (!unlockTimer) {
      return
    }

    clearTimeout(unlockTimer)
    unlockTimer = null
  }

  /**
   * Скролл к якорю с вычетом sticky SectionMenu и видимой шапки.
   * Нужен и для табов меню, и для нативного `#hash` (например `#medals`).
   */
  function scrollToHashTarget(href: string, duration = SCROLL_MS) {
    if (!href.startsWith('#')) {
      return false
    }

    const target = document.querySelector<HTMLElement>(href)

    if (!target) {
      return false
    }

    clearUnlockTimer()
    spy.lock()

    const targetTop = target.getBoundingClientRect().top + window.scrollY
    const expectHeader = targetTop < window.scrollY ? header.headerHeight.value : 0

    // Повторный вызов отменяет предыдущую анимацию в smoothScrollTo
    smoothScrollTo(
      Math.max(
        targetTop - sticky.menuHeight.value - expectHeader - header.previewHeight.value,
        0,
      ),
      { duration },
    )
    unlockTimer = setTimeout(() => {
      unlockTimer = null
      spy.unlockAndPick()
    }, duration)

    return true
  }

  function handleTabClick(item: SectionMenuItem) {
    if (!item.href) {
      return
    }

    if (!item.href.startsWith('#')) {
      window.location.assign(item.href)

      return
    }

    if (!scrollToHashTarget(item.href)) {
      if (item.redirectHref) {
        window.location.assign(item.redirectHref)
      }

      return
    }

    window.history.pushState(null, '', item.href)
    spy.syncHash()
  }

  function handleHashChange() {
    spy.syncHash()

    if (window.location.hash) {
      scrollToHashTarget(window.location.hash)
    }
  }

  onMounted(() => {
    header.bind()
    setupAll()

    resizeObserver = new ResizeObserver(() => {
      setupAllThrottled()
    })
    resizeObserver.observe(document.documentElement)

    window.addEventListener('hashchange', handleHashChange)
    window.addEventListener('popstate', setupAll)
  })

  onBeforeUnmount(() => {
    clearUnlockTimer()
    sticky.teardown()
    spy.teardown()
    header.unbind()
    resizeObserver?.disconnect()

    window.removeEventListener('hashchange', handleHashChange)
    window.removeEventListener('popstate', setupAll)
  })

  return {
    root: sticky.root,
    anchor: sticky.anchor,
    sentinel: sticky.sentinel,
    isPinned: sticky.isPinned,
    anchorStyle: sticky.anchorStyle,
    pinnedStyle: sticky.pinnedStyle,
    prevTabIndex: spy.prevTabIndex,
    activeTabIndex: spy.activeTabIndex,
    handleTabClick,
  }
}
